import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetCancellationsByReason } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useManagementKpiFilter from './useManagementKpiFilter';
import {
  ControlFigures,
  nf,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from './shared';

/**
 * P4b — avbokningar per orsak, så att ombokningar går att särskilja.
 *
 * Nekade uppdrag ligger inte här utan under Kvalitet → Nekade uppdrag, som redan är byggt med
 * anledning, tid före start och gruppledare.
 */
const CancellationReasonsWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } = useManagementKpiFilter();
  const getData = useKPIGetCancellationsByReason();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  const byReason = new Map<string, number>();
  (result?.dataPoints ?? []).forEach((dp) => {
    byReason.set(dp.cancelTypeName, (byReason.get(dp.cancelTypeName) ?? 0) + dp.cases);
  });
  const reasons = Array.from(byReason.entries())
    .map(([name, cases]) => ({ name, cases }))
    .sort((a, b) => b.cases - a.cases);

  const denominator = result?.totalCasesInPeriod ?? 0;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.CancellationReasons]}
      count={result?.totalCancelled ?? 0}
      isLoading={getData.isLoading}
      info="Avbokade kundnära ärenden (inköp, hemleverans, inbyte) per orsak, räknat på den dag jobbet skulle ha utförts. Andelen är av periodens bokade kundnära ärenden."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={reasons.length === 0}
      >
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Orsak</th>
              <th style={thNumStyle}>Antal</th>
              <th style={thNumStyle}>Andel av bokade</th>
            </tr>
          </thead>
          <tbody>
            {reasons.map((reason) => (
              <tr key={reason.name} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{reason.name}</td>
                <td style={tdNumStyle}>{nf(reason.cases)}</td>
                <td style={tdNumStyle}>
                  {denominator > 0
                    ? `${nf((reason.cases / denominator) * 100, 1)} %`
                    : '–'}
                </td>
              </tr>
            ))}
            <tr style={totalRowStyle}>
              <td style={tdStyle}>Totalt avbokade</td>
              <td style={tdNumStyle}>{nf(result?.totalCancelled ?? 0)}</td>
              <td style={tdNumStyle}>{nf(result?.totalCancelledPercentage ?? 0, 1)} %</td>
            </tr>
          </tbody>
        </table>

        <ControlFigures
          items={[
            `Nämnare: ${nf(denominator)} bokade kundnära ärenden i perioden`,
            'Ett ärende med flera avbokade etapper räknas en gång: under Bomkörning om någon etapp bomkördes, annars under den senast bokade etappens orsak',
            'Ombokningar bygger på att avbokningsorsaken faktiskt sätts — bokas ett ärende om genom att bara flytta datum finns ingen händelse att räkna',
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default CancellationReasonsWidget;
